import React, { useState, useEffect } from 'react';
import '../../css/OrphanedDocumentsModal.css';
import { auth } from '../../firebase';

const BASE_URL = 'https://bluebirddocumentationadmin.pythonanywhere.com';

const OrphanedDocumentsModal = ({ isOpen, onClose, uid }) => {
    const [loading, setLoading] = useState(false);
    const [orphanedData, setOrphanedData] = useState(null);

    // For missing_page_detail orphans — fixed via create-missing-page-details
    const [selectedSpaces, setSelectedSpaces] = useState([]);
    const [selectedMissingDetailPages, setSelectedMissingDetailPages] = useState([]);

    // For missing_parent orphans — fixed via reparent-orphaned-pages
    // Map of pageId -> chosen new parent name (empty string = make standalone)
    const [parentSelections, setParentSelections] = useState({});
    // Set of page IDs that are "ready" (user has made a selection)
    const [reparentReady, setReparentReady] = useState(new Set());

    const [availableParents, setAvailableParents] = useState([]);

    const [fixing, setFixing] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        if (isOpen) {
            fetchOrphanedDocuments();
            fetchAvailableParents();
        }
    }, [isOpen]);

    const getAuthHeaders = async () => {
        const user = auth.currentUser;
        if (!user) throw new Error('User not authenticated');
        const token = await user.getIdToken();
        return {
            'Content-Type': 'application/json',
            'Authorization': `${token}`,
            'Requesting-User-Uid': uid
        };
    };

    const fetchOrphanedDocuments = async () => {
        setLoading(true);
        setError(null);
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${BASE_URL}/documentation/orphaned-documents/`, {
                method: 'GET',
                headers
            });
            if (!response.ok) throw new Error('Failed to fetch orphaned documents');
            const data = await response.json();
            setOrphanedData(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableParents = async () => {
        try {
            const user = auth.currentUser;
            if (!user) return;
            const token = await user.getIdToken();
            const headers = { 'Auth-Token': `Bearer ${token}` };

            const [spacesRes, pagesRes] = await Promise.all([
                fetch(`${BASE_URL}/spaces/author/${uid}/`, { headers }),
                fetch(`${BASE_URL}/pages/author/${uid}/`, { headers })
            ]);

            const parents = [];
            if (spacesRes.ok) {
                const data = await spacesRes.json();
                const spaces = data.spaces || data;
                spaces.forEach(s => parents.push({ name: s.name, type: 'space' }));
            }
            if (pagesRes.ok) {
                const data = await pagesRes.json();
                const pages = data.pages || data;
                pages.forEach(p => parents.push({ name: p.page_name, type: 'page' }));
            }
            setAvailableParents(parents);
        } catch {
            // silently fail — parent dropdown just won't be populated
        }
    };

    // ---- missing_page_detail helpers ----

    const missingDetailPages = orphanedData
        ? orphanedData.orphaned_pages.filter(p => p.orphan_reason === 'missing_page_detail' || !p.orphan_reason)
        : [];

    const missingParentPages = orphanedData
        ? orphanedData.orphaned_pages.filter(p => p.orphan_reason === 'missing_parent')
        : [];

    const handleSpaceToggle = (id) => {
        setSelectedSpaces(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleMissingDetailPageToggle = (id) => {
        setSelectedMissingDetailPages(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleSelectAllSpaces = () => {
        setSelectedSpaces(
            selectedSpaces.length === orphanedData.orphaned_spaces.length
                ? []
                : orphanedData.orphaned_spaces.map(s => s.id)
        );
    };

    const handleSelectAllMissingDetailPages = () => {
        setSelectedMissingDetailPages(
            selectedMissingDetailPages.length === missingDetailPages.length
                ? []
                : missingDetailPages.map(p => p.id)
        );
    };

    // ---- missing_parent helpers ----

    const handleParentSelect = (pageId, value) => {
        setParentSelections(prev => ({ ...prev, [pageId]: value }));
        setReparentReady(prev => {
            const next = new Set(prev);
            // Any selection (including empty string = standalone) marks it ready
            next.add(pageId);
            return next;
        });
    };

    const handleSelectAllParents = (value) => {
        const updates = {};
        const ready = new Set(reparentReady);
        missingParentPages.forEach(p => {
            updates[p.id] = value;
            ready.add(p.id);
        });
        setParentSelections(prev => ({ ...prev, ...updates }));
        setReparentReady(ready);
    };

    // ---- fix actions ----

    const handleFixSelected = async () => {
        const hasPageDetailFixes = selectedSpaces.length > 0 || selectedMissingDetailPages.length > 0;
        const reparentItems = missingParentPages.filter(p => reparentReady.has(p.id));

        if (!hasPageDetailFixes && reparentItems.length === 0) {
            setError('Please select at least one document to fix');
            return;
        }

        setFixing(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const headers = await getAuthHeaders();
            const messages = [];

            // Fix missing PageDetail entries
            if (hasPageDetailFixes) {
                const response = await fetch(`${BASE_URL}/documentation/create-missing-page-details/`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        space_ids: selectedSpaces,
                        page_ids: selectedMissingDetailPages,
                        fix_all: false
                    })
                });
                if (!response.ok) throw new Error('Failed to fix missing PageDetail documents');
                const data = await response.json();
                messages.push(data.message);
            }

            // Fix missing-parent pages via reparent
            if (reparentItems.length > 0) {
                const reparentings = reparentItems.map(p => ({
                    page_id: p.id,
                    new_parent: parentSelections[p.id] || null
                }));
                const response = await fetch(`${BASE_URL}/documentation/reparent-orphaned-pages/`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ reparentings })
                });
                if (!response.ok) throw new Error('Failed to reparent orphaned pages');
                const data = await response.json();
                messages.push(data.message);
            }

            setSuccessMessage(messages.join(' | '));
            setSelectedSpaces([]);
            setSelectedMissingDetailPages([]);
            setParentSelections({});
            setReparentReady(new Set());
            setTimeout(() => fetchOrphanedDocuments(), 1000);
        } catch (err) {
            setError(err.message);
        } finally {
            setFixing(false);
        }
    };

    const handleFixAll = async () => {
        const hasMissingParent = missingParentPages.length > 0;
        const confirmMsg = hasMissingParent
            ? 'Fix All will create missing PageDetail entries and make all missing-parent pages standalone (no parent). Continue?'
            : 'Are you sure you want to fix ALL orphaned documents? This will create PageDetail entries for all orphaned spaces and pages.';

        if (!window.confirm(confirmMsg)) return;

        setFixing(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const headers = await getAuthHeaders();
            const messages = [];

            // Fix missing PageDetail entries
            const response = await fetch(`${BASE_URL}/documentation/create-missing-page-details/`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ fix_all: true })
            });
            if (!response.ok) throw new Error('Failed to fix all documents');
            const data = await response.json();
            messages.push(data.message);

            // Make all missing-parent pages standalone
            if (hasMissingParent) {
                const reparentings = missingParentPages.map(p => ({ page_id: p.id, new_parent: null }));
                const rRes = await fetch(`${BASE_URL}/documentation/reparent-orphaned-pages/`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ reparentings })
                });
                if (!rRes.ok) throw new Error('Failed to reparent missing-parent pages');
                const rData = await rRes.json();
                messages.push(rData.message);
            }

            setSuccessMessage(messages.join(' | '));
            setSelectedSpaces([]);
            setSelectedMissingDetailPages([]);
            setParentSelections({});
            setReparentReady(new Set());
            setTimeout(() => fetchOrphanedDocuments(), 1000);
        } catch (err) {
            setError(err.message);
        } finally {
            setFixing(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const totalSelected =
        selectedSpaces.length +
        selectedMissingDetailPages.length +
        Array.from(reparentReady).filter(id => missingParentPages.some(p => p.id === id)).length;

    if (!isOpen) return null;

    return (
        <div className="OrphanedDocumentsModal__overlay" onClick={onClose}>
            <div className="OrphanedDocumentsModal__container" onClick={(e) => e.stopPropagation()}>
                <div className="OrphanedDocumentsModal__header">
                    <h2>
                        <i className="fa-solid fa-triangle-exclamation"></i> Orphaned Documents
                    </h2>
                    <button
                        className="OrphanedDocumentsModal__close-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>

                <div className="OrphanedDocumentsModal__body">
                    {loading && (
                        <div className="OrphanedDocumentsModal__loading">
                            <i className="fa-solid fa-spinner fa-spin"></i>
                            <p>Loading orphaned documents...</p>
                        </div>
                    )}

                    {error && (
                        <div className="OrphanedDocumentsModal__error">
                            <i className="fa-solid fa-exclamation-circle"></i>
                            <p>{error}</p>
                        </div>
                    )}

                    {successMessage && (
                        <div className="OrphanedDocumentsModal__success">
                            <i className="fa-solid fa-check-circle"></i>
                            <p>{successMessage}</p>
                        </div>
                    )}

                    {!loading && orphanedData && (
                        <>
                            <div className="OrphanedDocumentsModal__info">
                                <p>
                                    <i className="fa-solid fa-info-circle"></i>
                                    Found <strong>{orphanedData.total_orphaned}</strong> orphaned documents
                                    ({orphanedData.orphaned_spaces.length} spaces, {orphanedData.orphaned_pages.length} pages)
                                </p>
                                {orphanedData.total_orphaned > 0 ? (
                                    <p className="OrphanedDocumentsModal__description">
                                        Select documents to fix. Pages with a missing parent require you to choose a new parent or make them standalone.
                                    </p>
                                ) : (
                                    <p className="OrphanedDocumentsModal__description">
                                        If you feel there are still missing documents that you are not seeing on your sidebar, please contact us!
                                    </p>
                                )}
                            </div>

                            {orphanedData.total_orphaned === 0 ? (
                                <div className="OrphanedDocumentsModal__no-orphans">
                                    <i className="fa-solid fa-check-circle"></i>
                                    <p>Great! No orphaned documents found.</p>
                                </div>
                            ) : (
                                <>
                                    {/* ---- Orphaned Spaces (missing PageDetail) ---- */}
                                    {orphanedData.orphaned_spaces.length > 0 && (
                                        <div className="OrphanedDocumentsModal__section">
                                            <div className="OrphanedDocumentsModal__section-header">
                                                <h3>
                                                    <i className="fa-solid fa-folder"></i> Orphaned Spaces ({orphanedData.orphaned_spaces.length})
                                                </h3>
                                                <button
                                                    className="OrphanedDocumentsModal__select-all-btn"
                                                    onClick={handleSelectAllSpaces}
                                                >
                                                    {selectedSpaces.length === orphanedData.orphaned_spaces.length ? 'Deselect All' : 'Select All'}
                                                </button>
                                            </div>
                                            <div className="OrphanedDocumentsModal__list">
                                                {orphanedData.orphaned_spaces.map(space => (
                                                    <div
                                                        key={space.id}
                                                        className={`OrphanedDocumentsModal__item ${selectedSpaces.includes(space.id) ? 'selected' : ''}`}
                                                        onClick={() => handleSpaceToggle(space.id)}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedSpaces.includes(space.id)}
                                                            onChange={() => handleSpaceToggle(space.id)}
                                                            className="OrphanedDocumentsModal__checkbox"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <div className="OrphanedDocumentsModal__item-content">
                                                            <div className="OrphanedDocumentsModal__item-name">
                                                                {space.name}
                                                                {space.is_public && (
                                                                    <span className="OrphanedDocumentsModal__badge public">
                                                                        <i className="fa-solid fa-globe"></i> Public
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="OrphanedDocumentsModal__item-details">
                                                                <span>Created: {formatDate(space.created_at)}</span>
                                                                {space.description && (
                                                                    <span className="OrphanedDocumentsModal__description">{space.description}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* ---- Pages missing PageDetail ---- */}
                                    {missingDetailPages.length > 0 && (
                                        <div className="OrphanedDocumentsModal__section">
                                            <div className="OrphanedDocumentsModal__section-header">
                                                <h3>
                                                    <i className="fa-solid fa-file"></i> Orphaned Pages — Missing Entry ({missingDetailPages.length})
                                                </h3>
                                                <button
                                                    className="OrphanedDocumentsModal__select-all-btn"
                                                    onClick={handleSelectAllMissingDetailPages}
                                                >
                                                    {selectedMissingDetailPages.length === missingDetailPages.length ? 'Deselect All' : 'Select All'}
                                                </button>
                                            </div>
                                            <div className="OrphanedDocumentsModal__list">
                                                {missingDetailPages.map(page => (
                                                    <div
                                                        key={page.id}
                                                        className={`OrphanedDocumentsModal__item ${selectedMissingDetailPages.includes(page.id) ? 'selected' : ''}`}
                                                        onClick={() => handleMissingDetailPageToggle(page.id)}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedMissingDetailPages.includes(page.id)}
                                                            onChange={() => handleMissingDetailPageToggle(page.id)}
                                                            className="OrphanedDocumentsModal__checkbox"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <div className="OrphanedDocumentsModal__item-content">
                                                            <div className="OrphanedDocumentsModal__item-name">
                                                                {page.page_name}
                                                                {page.is_public && (
                                                                    <span className="OrphanedDocumentsModal__badge public">
                                                                        <i className="fa-solid fa-globe"></i> Public
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="OrphanedDocumentsModal__item-details">
                                                                <span>Parent: {page.parent_page || 'None'}</span>
                                                                <span>Created: {formatDate(page.created_at)}</span>
                                                                {page.description && (
                                                                    <span className="OrphanedDocumentsModal__description">{page.description}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* ---- Pages with missing parent ---- */}
                                    {missingParentPages.length > 0 && (
                                        <div className="OrphanedDocumentsModal__section">
                                            <div className="OrphanedDocumentsModal__section-header">
                                                <h3>
                                                    <i className="fa-solid fa-file-circle-question"></i> Orphaned Pages — Missing Parent ({missingParentPages.length})
                                                </h3>
                                                <div className="OrphanedDocumentsModal__reparent-actions">
                                                    <button
                                                        className="OrphanedDocumentsModal__select-all-btn"
                                                        onClick={() => handleSelectAllParents('')}
                                                        title="Mark all as standalone (no parent)"
                                                    >
                                                        All Standalone
                                                    </button>
                                                </div>
                                            </div>
                                            <p className="OrphanedDocumentsModal__section-hint">
                                                <i className="fa-solid fa-circle-info"></i> These pages reference a parent that no longer exists. Choose a new parent or make them standalone.
                                            </p>
                                            <div className="OrphanedDocumentsModal__list">
                                                {missingParentPages.map(page => {
                                                    const isReady = reparentReady.has(page.id);
                                                    const currentSelection = parentSelections[page.id];
                                                    return (
                                                        <div
                                                            key={page.id}
                                                            className={`OrphanedDocumentsModal__item OrphanedDocumentsModal__item--reparent ${isReady ? 'ready' : ''}`}
                                                        >
                                                            <div className="OrphanedDocumentsModal__item-status">
                                                                {isReady
                                                                    ? <i className="fa-solid fa-circle-check" style={{ color: '#10b981', fontSize: '1.1rem' }}></i>
                                                                    : <i className="fa-solid fa-circle-exclamation" style={{ color: '#f59e0b', fontSize: '1.1rem' }}></i>
                                                                }
                                                            </div>
                                                            <div className="OrphanedDocumentsModal__item-content">
                                                                <div className="OrphanedDocumentsModal__item-name">
                                                                    {page.page_name}
                                                                    {page.is_public && (
                                                                        <span className="OrphanedDocumentsModal__badge public">
                                                                            <i className="fa-solid fa-globe"></i> Public
                                                                        </span>
                                                                    )}
                                                                    <span className="OrphanedDocumentsModal__badge OrphanedDocumentsModal__badge--warning">
                                                                        <i className="fa-solid fa-unlink"></i> Missing parent: {page.parent_page}
                                                                    </span>
                                                                </div>
                                                                <div className="OrphanedDocumentsModal__item-details">
                                                                    <span>Created: {formatDate(page.created_at)}</span>
                                                                    {page.description && (
                                                                        <span className="OrphanedDocumentsModal__description">{page.description}</span>
                                                                    )}
                                                                </div>
                                                                <div className="OrphanedDocumentsModal__parent-select-row">
                                                                    <label className="OrphanedDocumentsModal__parent-select-label">
                                                                        New parent:
                                                                    </label>
                                                                    <select
                                                                        className="OrphanedDocumentsModal__parent-select"
                                                                        value={currentSelection !== undefined ? currentSelection : '__unset__'}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value === '__standalone__' ? '' : e.target.value;
                                                                            if (e.target.value === '__unset__') return;
                                                                            handleParentSelect(page.id, val);
                                                                        }}
                                                                    >
                                                                        <option value="__unset__">— Select an action —</option>
                                                                        <option value="__standalone__">Make standalone (no parent)</option>
                                                                        {availableParents.length > 0 && (
                                                                            <optgroup label="Spaces">
                                                                                {availableParents
                                                                                    .filter(p => p.type === 'space')
                                                                                    .map(p => (
                                                                                        <option key={p.name} value={p.name}>{p.name}</option>
                                                                                    ))
                                                                                }
                                                                            </optgroup>
                                                                        )}
                                                                        {availableParents.filter(p => p.type === 'page').length > 0 && (
                                                                            <optgroup label="Pages">
                                                                                {availableParents
                                                                                    .filter(p => p.type === 'page' && p.name !== page.page_name)
                                                                                    .map(p => (
                                                                                        <option key={p.name} value={p.name}>{p.name}</option>
                                                                                    ))
                                                                                }
                                                                            </optgroup>
                                                                        )}
                                                                    </select>
                                                                    {isReady && (
                                                                        <span className="OrphanedDocumentsModal__ready-label">
                                                                            {currentSelection ? `→ ${currentSelection}` : '→ Standalone'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>

                {!loading && orphanedData && orphanedData.total_orphaned > 0 && (
                    <div className="OrphanedDocumentsModal__footer">
                        <button
                            className="OrphanedDocumentsModal__btn secondary"
                            onClick={onClose}
                            disabled={fixing}
                        >
                            Close
                        </button>
                        <button
                            className="OrphanedDocumentsModal__btn danger"
                            onClick={handleFixAll}
                            disabled={fixing}
                        >
                            {fixing ? (
                                <><i className="fa-solid fa-spinner fa-spin"></i> Fixing...</>
                            ) : (
                                <><i className="fa-solid fa-wand-magic-sparkles"></i> Fix All</>
                            )}
                        </button>
                        <button
                            className="OrphanedDocumentsModal__btn primary"
                            onClick={handleFixSelected}
                            disabled={fixing || totalSelected === 0}
                        >
                            {fixing ? (
                                <><i className="fa-solid fa-spinner fa-spin"></i> Fixing...</>
                            ) : (
                                <><i className="fa-solid fa-wrench"></i> Fix Selected ({totalSelected})</>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrphanedDocumentsModal;
